import type {
  IdType,
  PluginHook,
  TableInstance,
  TableOptions,
  TableState,
  UsePaginationInstanceProps,
  UsePaginationOptions,
  UsePaginationState,
  UseRowSelectInstanceProps,
  UseRowSelectOptions,
  UseRowSelectState,
  UseSortByInstanceProps,
  UseSortByOptions,
  UseSortByState
} from 'react-table';
import { usePagination, useTable } from 'react-table';
import _ from 'lodash';
import type { UseStore } from 'zustand';
import type { InfiniteScrollStore, PagingStore } from '../Store';
import { useEffect, useMemo, useState } from 'react';
import type {
  CustomColumn,
  PersistentColumnState,
  TableProps
} from '../../components';
import { showError } from '../Error';
import { isEmptyString } from '../../util';
import { persistentStateProvider } from '../PersistentStateProvider';

// eslint-disable-next-line @typescript-eslint/ban-types
type ParamType = object;
/**
 * Type for the TableInstance that is returned by the custom hooks.
 */
type HookTableInstanceType<T extends ParamType> = TableInstance<T> &
  UseRowSelectInstanceProps<T> &
  UsePaginationInstanceProps<T> &
  UseSortByInstanceProps<T> & {
    state: TableState<T> &
      UsePaginationState<T> &
      UseRowSelectState<T> &
      UseSortByState<T>;
  };

/**
 * Options for the {@link useInfiniteScrollTable} hook.
 */
type UseInfiniteScrollTableOptions<
  T extends ParamType,
  S extends InfiniteScrollStore<T, any>
> = Omit<TableOptions<T>, 'data'> &
  UseRowSelectOptions<T> &
  UseSortByOptions<T> & {
    /** Hook to access the store. */
    store: UseStore<S>;
    /** The initial state passed to the useStore hook. */
    initialState?: Partial<
      TableState<T> & UseRowSelectState<T> & UseSortByState<T>
    >;
  };

/**
 * Return type of the {@link useInfiniteScrollTable} hook.
 * These are the options passed to the Table component which the hook is meant to control.
 */
type UseInfiniteScrollTableReturnType<T extends ParamType> = Required<
  Pick<TableProps<T>, 'store'>
> & {
  /** Paging will always be 'infinite-scroll'. */
  paging: 'infinite-scroll';
  /** The tableInstance created with {@link useTable} */
  tableInstance: HookTableInstanceType<T>;
};

/**
 * A helper function to get the list of columns which should be hidden.
 *
 * @param columns options for all the columns
 * @param hiddenColumns columns that should normally be hidden initially
 * @return array containing all the hidden column ids
 */
function getHiddenColumns<T extends ParamType>(
  columns: Array<CustomColumn<T>>,
  hiddenColumns: Array<IdType<T>> = []
) {
  const hiddenPersisted = columns
    .filter(column => {
      // get all the columns that are hidden
      if (
        !isEmptyString(column.stateId) &&
        (column.persistState?.indexOf('hidden') ?? -1) >= 0
      ) {
        // get initial state from persistence
        const [
          persistentState
        ] = persistentStateProvider.getPersistentState<PersistentColumnState>(
          column.stateId
        );
        return persistentState?.hidden;
      }
      return false;
    })
    // map them to their unique identifier, so that this can be passed directly to the `hiddenColumns` initial state
    .map(column =>
      // using `id` property if the `accessor` is a function, like the react-table documentation demands
      typeof column.accessor === 'function' ? column.id : column.accessor
    );

  return [...hiddenColumns, ...hiddenPersisted];
}

/**
 * Custom hook to create the tableInstance created by the {@link useTable} react-table hook, used to control a
 * Table component. This is a convenience hook to make handling of 'infinite-scroll' Tables easier.
 *
 * @param params the {@link UseInfiniteScrollTableOptions} for the hook
 * @param plugins {@link useTable} plugins that should be added. Check the <a href="https://react-table.tanstack.com/docs/api/overview">react-table API</a>
 * for more information on what can be passed here.
 * @return {@link UseInfiniteScrollTableReturnType} containing Table component options (including the created tableInstance), which can be
 * directly destructured into a Table component.
 */
export function useInfiniteScrollTable<
  T extends ParamType,
  S extends InfiniteScrollStore<T, any>
>(
  {
    store: storeHook,
    initialState,
    ...options
  }: UseInfiniteScrollTableOptions<T, S>,
  ...plugins: Array<PluginHook<T>>
): UseInfiniteScrollTableReturnType<T> {
  const [pages] = storeHook(state => [state.pages]);
  //NOTE: Be aware that [[undefined], ["content"]] turns into [undefined, "content"] and it then throws an exception.
  const data = useMemo(() => _.flatten(pages) ?? [], [pages]);
  const tableInitialState = useMemo(
    () => ({
      ...initialState,
      hiddenColumns: getHiddenColumns(
        options.columns as Array<CustomColumn<T>>,
        initialState?.hiddenColumns
      )
    }),
    [initialState, options.columns]
  );

  const tableInstance = useTable(
    {
      data,
      initialState: tableInitialState,
      ...options
    } as TableOptions<T>,
    ...plugins
  ) as HookTableInstanceType<T>;
  return {
    tableInstance,
    paging: 'infinite-scroll',
    store: storeHook
  };
}

/**
 * Options for the {@link useInfiniteScrollTable} hook.
 */
type UsePagingTableOptions<
  T extends ParamType,
  S extends PagingStore<T, any>
> = Omit<TableOptions<T>, 'data'> &
  UseRowSelectOptions<T> &
  UseSortByOptions<T> &
  Omit<UsePaginationOptions<T>, 'manualPagination' | 'pageCount'> & {
    /** Hook to access the store. */
    store: UseStore<S>;
    /** The initial state passed to the useStore hook. */
    initialState?: Partial<
      TableState<T> &
        UseRowSelectState<T> &
        UseSortByState<T> &
        UsePaginationState<T>
    >;
  };

/**
 * Return type of the {@link usePagingTable} hook.
 * These are the options passed to the Table component which the hook is meant to control.
 */
type UsePagingTableReturnType<T extends ParamType> = {
  /** Paging will always be 'pages'. */
  paging: 'pages';
  /** The tableInstance created with {@link useTable} */
  tableInstance: HookTableInstanceType<T>;
};

/**
 * Custom hook to create the tableInstance created by the {@link useTable} react-table hook, used to control a
 * Table component. This is a convenience hook to make handling of paged Tables easier.
 *
 * @param params the {@link UsePagingTableOptions} for the hook
 * @param plugins {@link useTable} plugins that should be added. Check the <a href="https://react-table.tanstack.com/docs/api/overview">react-table API</a>
 * for more information on what can be passed here.
 * @return {@link UsePagingTableReturnType} containing Table component options (including the created tableInstance), which can be
 * directly destructured into a Table component.
 */
export function usePagingTable<
  T extends ParamType,
  S extends PagingStore<T, any>
>(
  { store: storeHook, initialState, ...options }: UsePagingTableOptions<T, S>,
  ...plugins: Array<PluginHook<T>>
): UsePagingTableReturnType<T> {
  const [
    pages,
    pageSize,
    totalCount,
    loadPage,
    wasLoaded,
    pageIndex
  ] = storeHook(state => [
    state.pages,
    state.pageSize,
    state.totalCount,
    state.loadPage,
    state.wasLoaded,
    state.pageIndex
  ]);
  const [data, setData] = useState<typeof pages[0]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const tableInitialState = useMemo(
    () => ({
      pageIndex: 0,
      pageSize: pageSize,
      ...initialState,
      hiddenColumns: getHiddenColumns(
        options.columns as Array<CustomColumn<T>>,
        initialState?.hiddenColumns
      )
    }),
    [initialState, options.columns, pageSize]
  );
  const tableInstance = useTable(
    {
      data,
      pageCount,
      manualPagination: true,
      initialState: tableInitialState,
      ...options
    } as TableOptions<T>,
    ...plugins,
    usePagination
  ) as HookTableInstanceType<T>;

  // update data/pageCount when store state changes
  useEffect(() => {
    setData(pages[pageIndex] ?? []);
    setPageCount(Math.ceil((totalCount ?? 0) / pageSize));
  }, [pageIndex, pageSize, pages, totalCount]);

  // this useEffect will handle loading of the pages when the tableInstance paging state changes
  // initial load of the store needs to happen somewhere on the outside
  // and if a load of a specific page happens without using the table pagination, then this effect will force a reload of the
  // current page of the table, which might possibly be a problem if a use-case for something like that exists
  useEffect(() => {
    if (wasLoaded && pageIndex !== tableInstance.state.pageIndex) {
      loadPage(tableInstance.state.pageIndex).catch(showError);
    }
  }, [loadPage, pageIndex, tableInstance.state.pageIndex, wasLoaded]);

  // go back to page 0 when the wasLoaded state of the store changes to false
  useEffect(() => {
    if (!wasLoaded) {
      tableInstance.gotoPage(0);
    }
  }, [tableInstance, wasLoaded]);

  return {
    tableInstance,
    paging: 'pages'
  };
}
